import React, { useCallback, useEffect, useState } from "react";
import { Ticket, Plus, Copy, Link2, Trash2, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function InvitesManager() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invites");
      setInvites(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load invites");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createInvite = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/invites");
      setInvites((arr) => [data, ...arr]);
      toast.success(`Invite code ${data.code} created`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create invite");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/invites/${id}`);
      setInvites((arr) => arr.filter((i) => i.id !== id));
      toast.success("Invite revoked");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not revoke invite");
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Unable to copy");
    }
  };

  const inviteLink = (code) => `${window.location.origin}/register?invite=${code}`;
  const available = invites.filter((i) => !i.used).length;

  return (
    <div className="vault-card p-6" data-testid="invites-manager">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-[Outfit] text-xl font-medium flex items-center gap-2"><Ticket size={18} /> Family invites</h2>
          <p className="text-sm text-[color:var(--vault-text-secondary)] mt-1">
            Generate a one-time code, then share the link with a family member. Each code works once.
          </p>
        </div>
        <Button
          onClick={createInvite}
          disabled={creating}
          className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white"
          data-testid="create-invite-btn"
        >
          <Plus size={16} className="mr-1.5" />
          {creating ? "Creating…" : "New invite"}
        </Button>
      </div>

      {!loading && invites.length > 0 && (
        <div className="text-xs text-[color:var(--vault-text-tertiary)] mt-3">
          {available} available · {invites.length - available} used
        </div>
      )}

      <div className="mt-4 space-y-2">
        {loading ? (
          <div className="text-sm text-[color:var(--vault-text-tertiary)] py-4">Loading invites…</div>
        ) : invites.length === 0 ? (
          <div className="text-sm text-[color:var(--vault-text-tertiary)] border border-dashed border-[color:var(--vault-border)] rounded-xl p-6 text-center" data-testid="invites-empty">
            No invites yet. Create one to invite a family member.
          </div>
        ) : (
          invites.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center gap-3 rounded-xl bg-[#F1F5FA] border border-[color:var(--vault-border)] px-3 py-2.5 flex-wrap"
              data-testid={`invite-row-${inv.code}`}
            >
              <span className="font-mono font-medium tracking-wider text-[color:var(--vault-text-primary)]">{inv.code}</span>
              {inv.used ? (
                <span className="inline-flex items-center gap-1 text-xs text-[color:var(--vault-text-tertiary)]">
                  <CheckCircle2 size={13} /> Used{inv.used_by_email ? ` by ${inv.used_by_email}` : ""}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-[color:var(--vault-success)]">
                  <Clock size={13} /> Available
                </span>
              )}
              <div className="ml-auto flex items-center gap-1">
                {!inv.used && (
                  <>
                    <button
                      onClick={() => copy(inv.code, "Code")}
                      title="Copy code"
                      className="p-2 rounded-lg text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-light)]"
                      data-testid={`copy-code-${inv.code}`}
                    >
                      <Copy size={15} />
                    </button>
                    <button
                      onClick={() => copy(inviteLink(inv.code), "Invite link")}
                      title="Copy invite link"
                      className="p-2 rounded-lg text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-light)]"
                      data-testid={`copy-link-${inv.code}`}
                    >
                      <Link2 size={15} />
                    </button>
                    <button
                      onClick={() => revoke(inv.id)}
                      title="Revoke"
                      className="p-2 rounded-lg text-[color:var(--vault-error)] hover:bg-[color:var(--vault-error-bg)]"
                      data-testid={`revoke-invite-${inv.code}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
